import React, { useState, useEffect, useRef } from 'react';

// Déclarations de type pour Google Analytics
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    [key: string]: any;
  }
}

interface CookieConsent {
  version: string;
  timestamp: number;
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
}

const COOKIE_CONSENT_KEY = 'totheroad_cookie_consent';
const COOKIE_CONSENT_VERSION = '1.0';
const CONSENT_DURATION_DAYS = 365;
const GA_MEASUREMENT_ID = 'G-HJWFG2WZFB';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showManageBtn, setShowManageBtn] = useState(false);
  const [analyticsCookies, setAnalyticsCookies] = useState(false);
  const [functionalCookies, setFunctionalCookies] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Initialiser au montage du composant
  useEffect(() => {
    initCookieConsent();
  }, []);

  // Fermer la modal quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && event.target === modalRef.current) {
        setShowModal(false);
      }
    };

    // Fermer avec la touche Échap
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Gérer le débordement du body quand la modal est ouverte
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const initCookieConsent = () => {
    const consent = getConsent();

    if (consent && consent.version === COOKIE_CONSENT_VERSION) {
      applyCookiePreferences(consent);
      setShowManageBtn(true);
    } else {
      setShowBanner(true);
    }
  };

  const getConsent = (): CookieConsent | null => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Erreur lors de la lecture du consentement:', e);
        return null;
      }
    }
    return null;
  };

  const saveConsent = (consent: CookieConsent) => {
    const expirationDate = new Date();
    expirationDate.setTime(
      expirationDate.getTime() + CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000
    );

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    document.cookie = `${COOKIE_CONSENT_KEY}=${JSON.stringify(consent)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
  };

  const applyCookiePreferences = (consent: CookieConsent) => {
    if (consent.analytics) {
      loadGoogleAnalytics();
    } else {
      blockGoogleAnalytics();
    }

    if (consent.functional) {
      enableFunctionalCookies();
    } else {
      disableFunctionalCookies();
    }
  };

  const loadGoogleAnalytics = () => {
    if (window.gtag) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    const gtag = (...args: any[]) => (window.dataLayer as any[]).push(args);
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    });

    window.gtag = gtag;
  };

  const blockGoogleAnalytics = () => {
    if (window.gtag) {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    }

    deleteCookie('_ga');
    deleteCookie('_gid');
    deleteCookie('_gat');

    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith('_ga_')) {
        const cookieName = cookie.split('=')[0];
        deleteCookie(cookieName);
      }
    }
  };

  const enableFunctionalCookies = () => {
    // Les données fonctionnelles sont lues directement par les pages concernées
  };

  const disableFunctionalCookies = () => {
    localStorage.removeItem('totheroad_contact_pref');
    deleteCookie('lang_pref');
    deleteCookie('display_mode');
  };

  const deleteCookie = (name: string) => {
    const domains = [window.location.hostname, '.' + window.location.hostname];
    const paths = ['/', '/fr/', '/en/'];

    domains.forEach((domain) => {
      paths.forEach((path) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
      });
    });
  };

  const openCookieModal = () => {
    const consent = getConsent();
    if (consent) {
      setAnalyticsCookies(consent.analytics);
      setFunctionalCookies(consent.functional);
    }
    setShowModal(true);
  };

  const closeCookieModal = () => {
    setShowModal(false);
  };

  const acceptAllCookies = () => {
    const consent: CookieConsent = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
      necessary: true,
      analytics: true,
      functional: true,
    };

    saveConsent(consent);
    applyCookiePreferences(consent);
    setShowBanner(false);
    setShowManageBtn(true);
  };

  const refuseAllCookies = () => {
    const consent: CookieConsent = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
      necessary: true,
      analytics: false,
      functional: false,
    };

    saveConsent(consent);
    applyCookiePreferences(consent);
    setShowBanner(false);
    setShowModal(false);
    setShowManageBtn(true);
  };

  const saveCustomPreferences = () => {
    const consent: CookieConsent = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
      necessary: true,
      analytics: analyticsCookies,
      functional: functionalCookies,
    };

    saveConsent(consent);
    applyCookiePreferences(consent);
    setShowBanner(false);
    setShowModal(false);
    setShowManageBtn(true);
  };

  return (
    <>
      {/* Bandeau de consentement */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6 shadow-lg z-[999999] animate-in slide-in-from-bottom duration-400">
          <div className="max-w-5xl mx-auto flex flex-col gap-5">
            <div className="flex items-start gap-3 sm:gap-8">
              <div className="text-2xl sm:text-4xl flex-shrink-0">🍪</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-400 mb-2">
                  Nous utilisons des cookies
                </h3>
                <p className="text-sm leading-relaxed text-gray-300">
                  Nous utilisons des cookies pour améliorer votre expérience de
                  navigation, analyser le trafic du site et personnaliser le
                  contenu. En cliquant sur "Tout accepter", vous consentez à
                  l'utilisation de TOUS les cookies. Vous pouvez également
                  personnaliser vos préférences.{' '}
                  <a
                    href="/cookies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 underline hover:text-blue-300 font-medium"
                  >
                    En savoir plus
                  </a>
                </p>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap justify-center sm:justify-end">
              <button
                onClick={refuseAllCookies}
                className="px-6 py-3 text-sm font-semibold rounded-lg border-2 border-red-600 text-red-400 hover:bg-red-600 hover:text-white transition-all duration-300"
              >
                Tout refuser
              </button>
              <button
                onClick={openCookieModal}
                className="px-6 py-3 text-sm font-semibold rounded-lg border-2 border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500 hover:text-white transition-all duration-300"
              >
                Personnaliser
              </button>
              <button
                onClick={acceptAllCookies}
                className="px-6 py-3 text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md hover:from-blue-700 hover:to-blue-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Tout accepter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de personnalisation */}
      {showModal && (
        <div
          ref={modalRef}
          className="fixed inset-0 bg-black/75 z-[1000000] flex items-center justify-center p-5 animate-in fade-in duration-300"
          id="cookieConsentModal"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in slide-in-from-bottom zoom-in-95 duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-5 md:p-7 rounded-t-2xl flex justify-between items-center sticky top-0">
              <h2 className="text-2xl font-semibold">🍪 Préférences de cookies</h2>
              <button
                onClick={closeCookieModal}
                className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-2xl transition-all duration-300 hover:rotate-90"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 md:p-8 space-y-5">
              {/* Cookies nécessaires */}
              <div className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-600 hover:shadow-md transition-all duration-300">
                <div className="flex justify-between items-start mb-3">
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">🔒</span>
                    Cookies strictement nécessaires
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="sr-only peer"
                    />
                    <div className="w-12 h-7 bg-gray-300 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-blue-600 peer-checked:to-blue-700"></div>
                    <div className="absolute left-1 top-1 bg-white w-5 h-5 rounded-full peer-checked:translate-x-5 transition-transform duration-300 shadow-sm"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  Ces cookies sont indispensables au fonctionnement du site. Ils
                  permettent notamment de sécuriser vos données, de mémoriser
                  votre panier de réservation et de gérer votre session de
                  navigation.
                </p>
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-900 text-xs font-semibold rounded-full">
                  Toujours actifs
                </span>
              </div>

              {/* Cookies d'analyse */}
              <div className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-600 hover:shadow-md transition-all duration-300">
                <div className="flex justify-between items-start mb-3">
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">📊</span>
                    Cookies d'analyse (Google Analytics)
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsCookies}
                      onChange={(e) => setAnalyticsCookies(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-7 bg-gray-300 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-blue-600 peer-checked:to-blue-700 transition-colors duration-300"></div>
                    <div className="absolute left-1 top-1 bg-white w-5 h-5 rounded-full peer-checked:translate-x-5 transition-transform duration-300 shadow-sm"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  Ces cookies nous permettent de mesurer l'audience du site,
                  d'analyser votre navigation et d'améliorer nos services. Nous
                  utilisons Google Analytics avec anonymisation des adresses IP.
                </p>
                <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-900 text-xs font-semibold rounded-full">
                  Optionnel
                </span>
              </div>

              {/* Cookies fonctionnels */}
              <div className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-600 hover:shadow-md transition-all duration-300">
                <div className="flex justify-between items-start mb-3">
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">🎯</span>
                    Cookies fonctionnels
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={functionalCookies}
                      onChange={(e) => setFunctionalCookies(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-7 bg-gray-300 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-blue-600 peer-checked:to-blue-700 transition-colors duration-300"></div>
                    <div className="absolute left-1 top-1 bg-white w-5 h-5 rounded-full peer-checked:translate-x-5 transition-transform duration-300 shadow-sm"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  Ces cookies permettent de mémoriser vos préférences (langue,
                  affichage) pour améliorer votre expérience lors de vos
                  prochaines visites.
                </p>
                <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-900 text-xs font-semibold rounded-full">
                  Optionnel
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 rounded-b-2xl flex gap-3 flex-col sm:flex-row sm:justify-end border-t border-gray-200">
              <button
                onClick={refuseAllCookies}
                className="px-6 py-3 border-2 border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-100 hover:border-gray-400 transition-all duration-300"
              >
                Tout refuser
              </button>
              <button
                onClick={saveCustomPreferences}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-semibold shadow-md hover:from-blue-700 hover:to-blue-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Enregistrer mes préférences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bouton "Gérer les cookies" */}
      {showManageBtn && (
        <button
          onClick={openCookieModal}
          aria-label="Gérer les cookies"
          className="fixed bottom-5 left-5 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 rounded-full text-sm font-semibold shadow-md hover:from-blue-700 hover:to-blue-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2 z-[999998]"
        >
          <span>🍪</span>
          <span>Gérer les cookies</span>
        </button>
      )}
    </>
  );
};

export default CookieConsent;
